const HOST = String.raw`[a-z][a-z0-9-]*-docker\.pkg\.dev`
const PROJECT = String.raw`[a-z][a-z0-9-]{4,28}[a-z0-9]`
const SEGMENT = String.raw`[a-z0-9]+(?:[._-][a-z0-9]+)*`

const IMAGE_PATH = new RegExp(
  `^(?:${HOST}/)?${PROJECT}/${SEGMENT}/${SEGMENT}(?:/${SEGMENT})*$`
)
const PROJECT_ID = new RegExp(`^${PROJECT}$`)
// The backend's location rule.
const LOCATION = /^[a-z][a-z0-9-]*[a-z0-9]$/

export const GCR_LOCATIONS = [
  "us",
  "europe",
  "asia",
  "us-central1",
  "us-east1",
  "us-east4",
  "us-west1",
  "us-west2",
  "northamerica-northeast1",
  "southamerica-east1",
  "europe-west1",
  "europe-west2",
  "europe-west3",
  "europe-west4",
  "asia-east1",
  "asia-northeast1",
  "asia-south1",
  "asia-southeast1",
  "australia-southeast1",
  "me-west1"
]

export const gcrHost = (location: string) => `${location}-docker.pkg.dev`

// Any location, not only the suggested ones, e.g. europe-west6.
export const normalizeGcrLocation = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/-docker\.pkg\.dev$/, "")

export const isValidGcrLocation = (location: string) => LOCATION.test(location)

// A registry row stores its paths without its own host.
export const stripGcrHost = (path: string, host: string) =>
  path.startsWith(`${host}/`) ? path.slice(host.length + 1) : path

export const normalizeGcrImagePath = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/@.*$/, "")
    .replace(/:[^/]*$/, "")
    .replace(/^\/+|\/+$/g, "")

// PROJECT/REPOSITORY/IMAGE, optionally with its registry host, as rows store base images.
export const isValidGcrImagePath = (path: string) => IMAGE_PATH.test(path)

export const isValidGcrProject = (project: string) =>
  PROJECT_ID.test(project.trim().toLowerCase())

const member = (email: string) => `serviceAccount:${email}`

export interface GcrGrantTarget {
  project?: string
  location?: string
  repository?: string
}

// What the form has so far fills the commands: the first valid base image (its host, if it
// has one, gives the location), else the first location and the project to check.
export const gcrGrantTarget = ({
  locations = [],
  repositories = [],
  projectId
}: {
  locations?: string[]
  repositories?: string[]
  projectId?: string
}): GcrGrantTarget => {
  const image = repositories
    .map(normalizeGcrImagePath)
    .find(isValidGcrImagePath)
  let location = locations.map(normalizeGcrLocation).find(isValidGcrLocation)
  let path = image
  const host = image?.match(new RegExp(`^(${HOST})/`))?.[1]
  if (image && host) {
    location = normalizeGcrLocation(host)
    path = image.slice(host.length + 1)
  }
  const [imageProject, repository] = path ? path.split("/") : []
  const typedProject = projectId?.trim().toLowerCase()

  return {
    project:
      imageProject ||
      (typedProject && isValidGcrProject(typedProject)
        ? typedProject
        : undefined),
    location,
    repository
  }
}

export const repositoryGrantCommand = (
  email: string,
  target: GcrGrantTarget = {}
) =>
  [
    `gcloud artifacts repositories add-iam-policy-binding ${target.repository || "REPOSITORY"} \\`,
    `  --project=${target.project || "PROJECT"} --location=${target.location || "LOCATION"} \\`,
    `  --member=${member(email)} \\`,
    "  --role=roles/artifactregistry.reader"
  ].join("\n")

export const projectGrantCommand = (
  email: string,
  target: GcrGrantTarget = {}
) =>
  [
    `gcloud projects add-iam-policy-binding ${target.project || "PROJECT"} \\`,
    `  --member=${member(email)} \\`,
    "  --role=roles/artifactregistry.reader"
  ].join("\n")
